import React, { useEffect } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { OriginThemeProvider } from "@origin-digital/ods-core";
import { QueryClient, QueryClientProvider } from "@origin-digital/query";
import { FeatureFlagContextProvider } from "@origin-digital/feature-flags";
import { AppContextProvider } from "./context/AppContext";
import { AppRoutes } from "./routes";
import { CustomLink } from "./components/CustomLink";
import { ErrorBoundary } from "./components/ErrorBoundary/ErrorBoundary";
import { config } from "./config";
import { GlobalStyles } from "./global-styles";

// Create QueryClient instance (no caching - staleTime: 0)
const queryClient = new QueryClient();
const EMPTY_FLAGS = {};

export const App: React.FunctionComponent<Record<string, unknown>> = React.memo(
  () => (
    <BrowserRouter>
      <ScrollToTop />
      <QueryClientProvider client={queryClient}>
        <OriginThemeProvider linkComponent={CustomLink} enableThemeSwitching>
          <FeatureFlagContextProvider
            featureFlags={config.featureFlags ?? EMPTY_FLAGS}
          >
            <AppContextProvider>
              <LocationAwareErrorBoundary>
                <AppRoutes />
              </LocationAwareErrorBoundary>
            </AppContextProvider>
          </FeatureFlagContextProvider>
        </OriginThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  )
);

const LocationAwareErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
};

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};
